/**
 * @fileoverview Toolbar content for the Documents Library Finder.
 *
 * Rendered inside the Finder's own toolbar row via its `toolbarStart` /
 * `toolbarEnd` slots — there is no separate Morgan toolbar row. The
 * Finder's built-in search and file-type filter are suppressed; Morgan's
 * flat cross-folder search and category filter take their place.
 *
 * `LibraryToolbarStart` (left, after the nav arrows + folder title):
 * the cross-folder search — the primary control, so it gets the wide,
 * prominent slot.
 *
 * `LibraryToolbarEnd` (right, after the view/sort controls): New Folder +
 * Add Files (hidden for viewers), category filter, uploading pill,
 * ProcessingPill, SyncStatusButton and the bulk-reprocess overflow menu
 * (also hidden for viewers).
 *
 * Every Morgan control shares one visual treatment matching the Finder's
 * native sort/view selects: 28px (`h-7`) outline controls on
 * `border-input` + `bg-background` (see TOOLBAR_BUTTON_CLASSNAME).
 */

import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ConvexError } from "convex/values";
import {
  FileSearch2,
  FolderPlus,
  ListFilter,
  Loader2,
  MoreHorizontal,
  RefreshCw,
  RotateCcw,
  Search,
  Upload,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import { cn } from "@/utils/misc";
import { SyncStatusButton } from "../SyncStatusButton";
import {
  CATEGORY_FILTER_KEYS,
  CATEGORY_LABELS,
  FAILED_FILTER_KEY,
  FAILED_FILTER_LABEL,
} from "../constants";
import { ProcessingPill } from "./ProcessingPill";
import { ACCEPTED_EXTENSIONS } from "./uploadHelpers";

const ACCEPT_ATTRIBUTE = [...ACCEPTED_EXTENSIONS].join(",");

// One shared treatment for every Morgan toolbar control, matching the
// Finder's native sort/view SelectTriggers (28px, border-input,
// bg-background). Used with `variant="outline"`.
const TOOLBAR_BUTTON_CLASSNAME =
  "h-7 shrink-0 gap-1.5 bg-background px-2 text-xs font-medium";

interface LibraryToolbarStartProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
}

/**
 * Leading Finder-toolbar content: the flat cross-folder search. It is the
 * Library's primary control, so it sits up front (after the folder title)
 * at a generous width, shrinking before anything else when the toolbar
 * tightens.
 */
export function LibraryToolbarStart({
  searchQuery,
  onSearchQueryChange,
}: LibraryToolbarStartProps) {
  return (
    <div className="relative w-72 min-w-28 shrink">
      <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        placeholder="Search documents..."
        value={searchQuery}
        onChange={(e) => onSearchQueryChange(e.target.value)}
        className="h-7 rounded-md pl-8 text-xs"
      />
    </div>
  );
}

interface LibraryToolbarEndProps {
  jobId: Id<"jobs">;
  isViewer: boolean;
  /** Folder new folders/files land in. Undefined targets the Library root. */
  currentFolderId?: Id<"folders">;
  /** Called with files chosen via the Add Files picker. */
  onPickFiles: (files: File[]) => void;
  categoryFilter: string;
  onCategoryFilterChange: (filter: string) => void;
  /** Documents whose processing pipeline failed (for the status filter). */
  failedCount: number;
  /** Accepted files still uploading (drop or picker). */
  uploadingCount: number;
}

/**
 * Trailing Finder-toolbar content: New Folder + Add Files (hidden for
 * viewers), the category filter (replacing the Finder's file-type filter)
 * and the processing/sync status affordances. The bulk-actions overflow
 * menu is hidden for viewers too.
 */
export function LibraryToolbarEnd({
  jobId,
  isViewer,
  currentFolderId,
  onPickFiles,
  categoryFilter,
  onCategoryFilterChange,
  failedCount,
  uploadingCount,
}: LibraryToolbarEndProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [newFolderOpen, setNewFolderOpen] = useState(false);

  const { data: categoryCounts } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.documents.queries.getDocumentCategoryCounts,
      { jobId },
    ),
  );
  const countByCategory = new Map(
    (categoryCounts ?? []).map((c) => [c.category, c.count]),
  );

  const hasActiveFilter = categoryFilter !== "all";
  const isFailedFilter = categoryFilter === FAILED_FILTER_KEY;

  return (
    <>
      {!isViewer && (
        <>
          <Button
            variant="outline"
            size="sm"
            className={TOOLBAR_BUTTON_CLASSNAME}
            onClick={() => setNewFolderOpen(true)}
          >
            <FolderPlus className="h-3.5 w-3.5" />
            New Folder
          </Button>
          <Button
            variant="default"
            size="sm"
            className="h-7 shrink-0 gap-1.5 px-2 text-xs font-medium"
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload className="h-3.5 w-3.5" />
            Add Files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTRIBUTE}
            className="hidden"
            onChange={(e) => {
              if (e.target.files?.length) {
                onPickFiles(Array.from(e.target.files));
                e.target.value = "";
              }
            }}
          />
        </>
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              TOOLBAR_BUTTON_CLASSNAME,
              // An active category reads on the trigger itself ("All
              // Documents" clears it from inside the menu).
              hasActiveFilter && "bg-accent hover:bg-accent",
              isFailedFilter &&
                "border-destructive/40 text-destructive hover:text-destructive",
            )}
          >
            <ListFilter className="h-3.5 w-3.5" />
            <span className="max-w-32 truncate">
              {isFailedFilter
                ? FAILED_FILTER_LABEL
                : hasActiveFilter
                  ? (CATEGORY_LABELS[categoryFilter] ?? categoryFilter)
                  : "Filter"}
            </span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="max-h-96 w-64 overflow-y-auto"
        >
          <DropdownMenuItem
            onClick={() => onCategoryFilterChange("all")}
            className={cn(!hasActiveFilter && "bg-accent")}
          >
            {CATEGORY_LABELS.all}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => onCategoryFilterChange(FAILED_FILTER_KEY)}
            className={cn(
              "justify-between gap-2",
              isFailedFilter && "bg-accent",
              failedCount > 0 && "text-destructive focus:text-destructive",
            )}
          >
            <span className={cn(failedCount === 0 && "text-muted-foreground")}>
              {FAILED_FILTER_LABEL}
            </span>
            <Badge
              variant={failedCount > 0 ? "destructive" : "secondary"}
              className="tabular-nums"
            >
              {failedCount}
            </Badge>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {CATEGORY_FILTER_KEYS.filter((key) => key !== "all").map((key) => {
            const count = countByCategory.get(key) ?? 0;
            return (
              <DropdownMenuItem
                key={key}
                onClick={() => onCategoryFilterChange(key)}
                className={cn(
                  "justify-between gap-2",
                  categoryFilter === key && "bg-accent",
                )}
              >
                <span className={cn(count === 0 && "text-muted-foreground")}>
                  {CATEGORY_LABELS[key]}
                </span>
                <Badge variant="secondary" className="tabular-nums">
                  {count}
                </Badge>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {uploadingCount > 0 && (
        <span className="inline-flex shrink-0 items-center whitespace-nowrap rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
          Uploading {uploadingCount}…
        </span>
      )}
      <ProcessingPill jobId={jobId} />
      <SyncStatusButton jobId={jobId} />
      {!isViewer && <BulkActionsMenu jobId={jobId} />}

      <NewFolderDialog
        jobId={jobId}
        parentFolderId={currentFolderId}
        open={newFolderOpen}
        onOpenChange={setNewFolderOpen}
      />
    </>
  );
}

/**
 * Overflow menu with the bulk reprocess actions that lived on the retired
 * BulkProcessingStatus bar. Same fire-and-toast UX (no confirm step).
 */
function BulkActionsMenu({ jobId }: { jobId: Id<"jobs"> }) {
  const { data: stats } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.extraction.queries.getJobProcessingStats,
      { jobId },
    ),
  );

  const { mutateAsync: retryAll, isPending: isRetrying } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.gatherInformation.documents.mutations.retryAllFailed,
    ),
  });
  const { mutateAsync: reprocessAll, isPending: isReprocessingAll } =
    useMutation({
      mutationFn: useConvexMutation(
        api.jobs.gatherInformation.documents.mutations.reprocessAllDocuments,
      ),
    });
  const { mutateAsync: rerunAllMetadata, isPending: isRerunningMetadata } =
    useMutation({
      mutationFn: useConvexMutation(
        api.jobs.gatherInformation.documents.mutations
          .rerunAllMetadataExtractions,
      ),
    });

  const isAnyRunning = isRetrying || isReprocessingAll || isRerunningMetadata;
  const failed = stats?.failed ?? 0;
  const total = stats?.total ?? 0;

  const handleRetryAll = async () => {
    try {
      await retryAll({ jobId });
      toast.success("Retrying failed documents...");
    } catch {
      toast.error("Failed to retry documents");
    }
  };

  const handleRerunAllMetadata = async () => {
    try {
      await rerunAllMetadata({ jobId });
      toast.success("Re-extracting metadata for all classified documents...");
    } catch {
      toast.error("Failed to start metadata re-extraction");
    }
  };

  const handleReprocessAll = async () => {
    try {
      await reprocessAll({ jobId });
      toast.success(`Re-processing ${total} documents...`);
    } catch {
      toast.error("Failed to start re-processing");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-7 shrink-0 bg-background px-0"
          aria-label="Bulk processing actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[200px]">
        <DropdownMenuItem
          disabled={failed === 0 || isAnyRunning}
          onClick={() => void handleRetryAll()}
        >
          {isRetrying ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RotateCcw className="mr-2 h-4 w-4" />
          )}
          Retry Failed{failed > 0 ? ` (${failed})` : ""}
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isAnyRunning || total === 0}
          onClick={() => void handleRerunAllMetadata()}
          title="Re-run category-specific metadata extraction for all classified documents"
        >
          {isRerunningMetadata ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <FileSearch2 className="mr-2 h-4 w-4" />
          )}
          Re-extract Metadata
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isAnyRunning || total === 0}
          onClick={() => void handleReprocessAll()}
          title="Re-run full pipeline (extract, classify, metadata, index) for all documents"
        >
          {isReprocessingAll ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          Re-run All
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Small dialog collecting a folder name; creates the folder inside the
 * currently open Finder folder (or the Library root). Exported so the
 * Finder's folder context menu ("New subfolder…") reuses it.
 */
export function NewFolderDialog({
  jobId,
  parentFolderId,
  open,
  onOpenChange,
}: {
  jobId: Id<"jobs">;
  parentFolderId?: Id<"folders">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState("");

  const { mutateAsync: createFolder, isPending } = useMutation({
    mutationFn: useConvexMutation(
      api.jobs.gatherInformation.folders.mutations.createFolder,
    ),
  });

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed || isPending) return;
    try {
      await createFolder({ jobId, name: trimmed, parentFolderId });
      toast.success(`Folder "${trimmed}" created`);
      setName("");
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof ConvexError && typeof err.data === "string"
          ? err.data
          : "Failed to create folder",
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>New Folder</DialogTitle>
          <DialogDescription>
            Create a folder inside the currently open location.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleCreate();
          }}
        >
          <Input
            autoFocus
            placeholder="Folder name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isPending}>
              {isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
